import { Redirect } from "@redotech/react-router-util/redirect";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { reviewsRoutes } from "@redotech/redo-merchant-review-app/route";
import { matchPath, Navigate } from "react-router-dom";
import {
  MARKETING_ANALYTICS_TABS_URL_PARAM,
  MarketingAnalyticsPage,
} from "../../../analytics/marketing/marketing-analytics";
import {
  automationRoutes,
  backwardsCompatibleEmailSmsRoutes,
  emailSmsRoutes,
  loyaltyRoutes,
} from "../../../marketing/route";

const HIDE_PADDING_ROUTES: string[] = [
  "/reviews/all-reviews",
  "/reviews/products",
  "/email-sms/audience",
  "/email-sms/audience/customers/:customerId",
  "/automations/*",
  "/email-sms/campaigns/*",
  "/email-sms/segments/*",
  "/email-sms/templates/email/*",
  "/email-sms/templates/sms/*",
  "/email-sms/templates/click-map/*",
  "/email-sms/campaigns/:campaignId/details",
  "/email-sms/signups/*",
  "/email-sms/ai-personas/*",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/members",
  "/loyalty/page/customize",
  "/tracking/tracking-page/tracking-page-builder",
  "/tracking/orders",
  "/email-sms/deliverability/*",
];

function hidePadding(url: string, search: { search: string }): boolean {
  if (
    url.includes("/marketing/analytics") &&
    new URLSearchParams(search.search).get(
      MARKETING_ANALYTICS_TABS_URL_PARAM,
    ) === MarketingAnalyticsPage.BACK_IN_STOCK
  ) {
    return true;
  }

  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const FULLSCREEN_ROUTES: string[] = [
  "/upsell-page/:id",
  "/email-sms/signups/builder/:id",
  "/email-sms/templates/email/:id",
  "/email-sms/templates/sms/:id",
  "/email-sms/templates/click-map/:templateId",
  "/email-sms/segments/:segmentId",
  "/email-sms/campaigns/:campaignId/edit/*",
  "/email-sms/campaigns/:campaignId/edit-with-ai/template",
  "/automations/:automationId",
  //  "/tracking/tracking-page",
  "/reviews/reviews-displays-widgets/review-form",
  "/reviews/reviews-displays-widgets/star-rating-widget",
  "/loyalty/page/customize",
  "/tracking/tracking-page/tracking-page-builder",
];

const HIDE_HEADER_ROUTES: string[] = ["/tracking/about", ...FULLSCREEN_ROUTES];
function hideHeader(url: string): boolean {
  return HIDE_HEADER_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

const HIDE_NAV_ROUTES: string[] = [...FULLSCREEN_ROUTES];

function hideNav(url: string): boolean {
  return HIDE_NAV_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/marketing${route}`, url),
  );
}

export const marketingRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Marketing" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <Page
            hideHeader={hideHeader}
            hideNavbar={hideNav}
            hidePadding={hidePadding}
          />
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="email-sms" /> },
      {
        path: "welcome",
        handle: { breadcrumb: "Welcome" },
        async lazy() {
          const { MarketingWelcomePage } = await import(
            "@redotech/redo-merchant-marketing-app/welcome/marketing-welcome-page"
          );
          return { Component: MarketingWelcomePage };
        },
      },
      {
        path: "analytics",
        handle: { breadcrumb: "Analytics" },
        async lazy() {
          const { MarketingAnalytics } = await import(
            "@redotech/redo-merchant-app/analytics/marketing/marketing-analytics"
          );
          return { Component: MarketingAnalytics };
        },
      },
      { path: "reviews", children: reviewsRoutes },
      { path: "loyalty", children: loyaltyRoutes },
      {
        path: "tracking",
        children: [
          {
            index: true,
            lazy: async () => {
              const { OrderTrackingLandingPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/order-tracking-landing-page"
              );
              return { Component: OrderTrackingLandingPage };
            },
          },
          {
            path: "about",
            handle: { breadcrumb: "Tracking", breadcrumbNavigate: false },
            async lazy() {
              const { AboutTracking } = await import(
                "@redotech/redo-merchant-app/setting/about-tracking/about-tracking"
              );
              return { element: <AboutTracking /> };
            },
          },
          {
            path: "orders",
            handle: { breadcrumb: "Orders" },
            lazy: async () => {
              const { Orders } = await import(
                "@redotech/redo-merchant-app/order/orders-table/orders-table"
              );
              return {
                element: (
                  <Orders
                    filterParams={(filters) => ({
                      dateRange: filters.dateRange,
                      shipmentStatus: filters.shipmentStatus,
                      carrier: filters.carrier,
                      destinationState: filters.destinationState,
                      destinationCountry: filters.destinationCountry,
                      originState: filters.originState,
                      originCountry: filters.originCountry,
                      dateFilter: filters.dateFilter,
                    })}
                  />
                ),
              };
            },
          },
          {
            path: "tracking-page/tracking-page-builder",
            lazy: async () => {
              const { TrackingPageBuilderPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page-builder"
              );
              return { Component: TrackingPageBuilderPage };
            },
          },
          {
            path: "tracking-page",
            handle: { breadcrumb: "Tracking page" },
            lazy: async () => {
              const { TrackingPage } = await import(
                "@redotech/redo-merchant-marketing-app/order-tracking/tracking-page/tracking-page"
              );
              return { Component: TrackingPage };
            },
          },
          {
            handle: { breadcrumb: "Discount flow" },
            path: "discount-flow",
            lazy: async () => {
              const { DiscountFlow } = await import(
                "@redotech/redo-merchant-app/setting/discount-flow/discount-flow"
              );
              return { element: <DiscountFlow /> };
            },
          },
          {
            handle: { breadcrumb: "Settings" },
            path: "settings",
            lazy: async () => {
              const { OrderTrackingSettings } = await import(
                "@redotech/redo-merchant-app/setting/order-tracking-settings/settings/settings"
              );
              return { Component: OrderTrackingSettings };
            },
          },
        ],
      },
      { path: "email-sms", children: emailSmsRoutes },
      { path: "automations", children: automationRoutes },
      {
        path: "email-builder/click-map/:templateId",
        handle: { breadcrumb: "Click Map" },
        async lazy() {
          const { FullscreenClickMap } = await import(
            "@redotech/redo-merchant-marketing-app/email-builder/click-map/fullscreen-click-map"
          );
          return { Component: FullscreenClickMap };
        },
      },
      ...backwardsCompatibleEmailSmsRoutes,
    ],
  },
];

export const backwardsCompatibleMarketingRoutes: PermissionedRouteObject[] = [
  {
    path: "reviews",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/reviews/} replaceValue="marketing/reviews" />
            ),
          },
        ],
      },
    ],
  },
  {
    path: "tracking",
    children: [
      {
        children: [
          {
            index: true,
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
          {
            path: "*",
            element: (
              <Redirect match={/tracking/} replaceValue="marketing/tracking" />
            ),
          },
        ],
      },
    ],
  },
];
